import deepEqual from 'deep-equal';
import Authn from './authn';
import NullTracker from './null-tracker';
import User from './user';
import Utils from './utils';

export default class Tracker {
  constructor({
    analytics,
    bluekaiAnalytics = NullTracker,
    msAnalytics = NullTracker,
    lighthouse,
  }) {
    this.analytics = analytics;
    this.bluekaiAnalytics = bluekaiAnalytics;
    this.msAnalytics = msAnalytics;
    this.lighthouse = lighthouse;
    this.delayMs = 500;
    this.user = new User(this.getAnonymousId());
  }

  /**
    Method to send event data to tracking service
  * */
  fireEvent(eventName, options = {}, callback = null) {
    if (!eventName) {
      throw new Error('eventName must be present!');
    }

    try {
      const newOptions = options;
      newOptions.auth_status = Authn.isAuthenticated();
      this.msAnalytics.track('event', eventName, newOptions, this.user.getJson());
      this.analytics.track(eventName, options);
      this.bluekaiAnalytics.track(options);
    } catch (err) {
      console.log('tracker.FireEvent Error:', err);
    } finally {
      this._executeCallback(callback);
    }
  }

  /**
    Method to send user level data to tracking service
  * */
  setUserProperties(options, callback = null) {
    this.analytics.identify(options);
    this.user.setProperties(options);
    this._executeCallback(callback);
  }

  /**
    Method to check for the Lighthouse cookie and assign experiment values
  * */
  checkForExperimentCookie() {
    if (!this.lighthouse || !this.lighthouse.isCookieSet()) {
      return;
    }

    const lighthouseExperiments = this.lighthouse.getExperiments();
    this.user.setExperiments(lighthouseExperiments);
    if (!this._isExperimentValueSet()) {
      this._setUserExperimentValue(lighthouseExperiments);
      return;
    }

    const userExperiments = this._getUserExperiments();

    if (!deepEqual(userExperiments, lighthouseExperiments)) {
      this._setUserExperimentValue(lighthouseExperiments);
    }
  }

  /**
    Method to send Page View event
  * */
  pageView(data = {}) {
    this.analytics.page(data);
    this.msAnalytics.track('page', 'PageView', data, this.user.getJson());
  }

  /**
    Add experiment in the user attribute.
  * */
  addExperiment(experimentId, value) {
    if (!experimentId || !value) {
      throw new Error('experimentId and value should be present');
    }

    this.user.addExperiment(experimentId, value);
    this.lighthouse.addExperiment(experimentId, value);
    const userExperiments = this._getUserExperiments() || {};
    userExperiments[experimentId] = value;
    this._setUserExperimentValue(userExperiments);
  }

  /**
    Get the generated anonymous user id for the user
  * */
  getAnonymousId() {
    return this.analytics.user().anonymousId();
  }

  /**
    Method to execute methods given by a queue
  * */
  executeQueuedEvents(queue) {
    if (!queue || queue.length < 1) {
      return;
    }

    const trackerScope = this;
    queue.forEach((e) => {
      const methodName = e[0];
      const args = e.slice(1);
      trackerScope[methodName](...args);
    });
  }

  _setUserExperimentValue(experiments) {
    const experimentArray = Utils.jsonCookieDataToArray(experiments);
    this.analytics.identify({
      experiment_value: experimentArray.join(),
    });
  }

  _isExperimentValueSet() {
    const userTraits = this._getUserTraits();
    return typeof (userTraits.experiment_value) !== 'undefined';
  }

  _getUserTraits() {
    return this.analytics.user().traits();
  }

  _getUserExperiments() {
    const userTraits = this._getUserTraits();
    if (!userTraits.experiment_value) {
      return null;
    }

    return Utils.transformCookieDataToJson(userTraits.experiment_value);
  }

  _executeCallback(callback) {
    if (!Utils.isFunction(callback)) {
      return;
    }

    Utils.delay(this.delayMs).then(() => {
      callback();
    });
  }
}
